import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import {
	AuthContext,
	AuthData,
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
} from '../context/auth';
import { ConfigContext, ConfigData } from '../context/config';
import { Payment } from '../types/payments';
import { FastForm } from './fastForm';
import { FORM_NAME } from './fastForm/data/constants';
import {
	getDefaultPolicyValues,
	getPaymentForm,
} from './fastForm/formBuilders';
import { PaymentFormValues } from './fastForm/formBuilders/getPaymentForm';
import { getValidationSchemaWithoutResolver } from './fastForm/utils/getValidationSchema';
import { paymentSchema } from './payments/lib/paymentSchema';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	payment: Payment;
}

const PaymentDetails = (props: Props) => {
	const { authData, configData, payment } = props;
	const {
		paymentConfiguration: { paymentConfiguration },
	} = configData;
	const timezone = authData.auth?.timezone;
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];

	const paymentFields = paymentConfiguration.fields;

	const form = getPaymentForm(
		{
			payment: payment,
			fields: paymentFields,
			fieldValues: payment?.fieldValues || {},
			mediaByLocator: {},
			timezone,
			multiCurrencyEnabled,
		},
		FORM_NAME.payment,
	);

	const paymentFormValues = getDefaultPolicyValues(form, authData);

	const specialFieldValues = {
		amountCurrency: payment?.amountCurrency || '',
		amount: payment?.amount || '',
		policyholderLocator: payment?.policyholderLocator || '',
		policyLocator: payment?.policyLocator || '',
	};

	const defaultValues = {
		payment: {
			...(paymentFormValues.payment as object),
			...specialFieldValues,
		},
	} as PaymentFormValues;

	const fallbackResolver: Resolver<any> = async () => ({
		values: {},
		errors: {},
	});

	if (!form) {
		return null;
	}

	const methods = useForm<PaymentFormValues>({
		defaultValues,
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			yupResolver(
				getValidationSchemaWithoutResolver(form).concat(paymentSchema(true)),
			) || fallbackResolver,
	});

	return (
		<>
			<FormProvider {...methods} key={payment.locator}>
				<FastForm
					formName={FORM_NAME.payment}
					form={form}
					productName={null} // payments are separate from product config and therefore this is not needed
					configVersion='' // also not needed as payment config is not versioned
					authData={authData}
					disabled
				/>
			</FormProvider>
		</>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<Component {...props} authData={authData} configData={configData} />
					)}
				</ConfigContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(PaymentDetails);
